import React, { Fragment } from "react";
import { Link } from "react-router-dom";
import { DataTable } from "@cxagroup/casper";
import { injectIntl } from "react-intl";
import messages from "components/messagesDescription";
import lodash from "lodash";
import { parseTemplate } from "cxa-utils";
import humps from "humps";
import { FormattedCustomDate } from "components/common/FormattedComponents";
import { renderAsHTML } from "../../../utils";
import AcceptedIcon from "../../../assets/images/svg/claim_status_accepted.svg";
import DraftIcon from "../../../assets/images/svg/claim_status_draft.svg";
import DeletedIcon from "../../../assets/images/svg/claim_status_void.svg";
import RejectedIcon from "../../../assets/images/svg/claim_status_rejected.svg";
import PendingIcon from "../../../assets/images/svg/claim_status_pending.svg";
import PaidIcon from "../../../assets/images/svg/claim_status_paid.svg";
import PendingPaymentIcon from "../../../assets/images/svg/claim_status_pending_payment.svg";
import PendingUserIcon from "../../../assets/images/svg/claim_status_pending_user.svg";
import StandardMoney from "../../common/StandardMoney";
import { useDispatch } from "react-redux";
import { setDelegatedEmployees } from "../../../actions/claimActionsPP";

const getFormattedValueMap = (row, intl) => ({
  date: value => {
    if (value && lodash.isString(value) && value.includes("$")) {
      return "";
    }
    return value ? <FormattedCustomDate value={value} /> : "";
  },
  currency: value => {
    if (!value || (lodash.isString(value) && value.includes("$"))) {
      value = 0.0;
    }
    return intl.formatNumber(value);
  },
  link: value => {
    row.href = `/claims/edit/${row.objectId}`;
    return value ? intl.formatMessage({ id: value, defaultMessage: value }) : "";
  },
});

const renderStatusIcon = (claimStatus, displayLabel, intl) => {
  switch (claimStatus) {
    case "Pending":
      return (
        <div className="status-icon-container">
          <div>
            <PendingIcon />
          </div>
          <span className="status-text">{displayLabel}</span>
        </div>
      );
    case "Accepted":
      return (
        <div className="status-icon-container">
          <div>
            <AcceptedIcon />
          </div>
          <span className="status-text">{displayLabel}</span>
        </div>
      );
    case "Rejected":
      return (
        <div className="status-icon-container">
          <div>
            <RejectedIcon />
          </div>
          <span className="status-text">{displayLabel}</span>
        </div>
      );
    case "Paid":
      return (
        <div className="status-icon-container">
          <div>
            <PaidIcon />
          </div>
          <span className="status-text">{displayLabel}</span>
        </div>
      );
    case "Void":
      return (
        <div className="status-icon-container">
          <div>
            <DeletedIcon />
          </div>
          <span className="status-text">{displayLabel}</span>
        </div>
      );
    case "PendingUser":
      return (
        <div className="status-icon-container">
          <div style={{ display: "grid" }}>
            <PendingIcon />
            <PendingUserIcon />
          </div>
          <div style={{ display: "grid", marginLeft: "10px" }}>
            <span>{displayLabel}</span>
            <span style={{ fontSize: "0.75rem" }}>
              {intl.formatMessage(messages.claims.moreInformationRequiredDescription)}
            </span>
          </div>
        </div>
      );
    case "Draft":
      return (
        <div className="status-icon-container">
          <div>
            <DraftIcon />
          </div>
          <span className="status-text">{displayLabel}</span>
        </div>
      );
    case "PendingPayment":
      return (
        <div className="status-icon-container">
          <div>
            <PendingPaymentIcon />
          </div>
          <span className="status-text">{displayLabel}</span>
        </div>
      );
    default:
      break;
  }
};

const PendingClaimsTable = injectIntl(
  ({
    claims,
    isInsuranceClaim,
    columns,
    action,
    selectedDelegator,
    getDonateCategory,
    newUIFlag = false,
    intl,
    type,
    employeeDelegator,
    history,
  }) => {
    const dispatch = useDispatch();

    if (!columns || columns.length <= 0) {
      return null;
    }

    const headers = columns.map(column => {
      if (
        !column.shouldDisplay ||
        (column.fieldID === "EstimatedPaymentDate" && isInsuranceClaim)
      ) {
        return null;
      }

      return intl.formatMessage({
        id: column.displayLabel,
        defaultMessage: column.displayLabel,
      });
    });
    // extra column for actions
    headers.push("");

    const rows = claims.map(({ objectId, editable, deletable, currencyType, ...item }) => {
      if (!columns || columns.length < 1) {
        return null;
      }

      const pathView =
        selectedDelegator &&
        selectedDelegator.toLowerCase() !== "myself" &&
        selectedDelegator.toLowerCase() !== "me"
          ? `/${getDonateCategory ? "donations" : "claims"}/view/${objectId}/${selectedDelegator}`
          : `/${getDonateCategory ? "donations" : "claims"}/view/${objectId}`;

      const handleReviewClaim = employeeDelegator => {
        dispatch(setDelegatedEmployees(employeeDelegator));
        history.push(pathView);
      };

      const row = columns.map((column, i) => {
        if (
          !column.shouldDisplay ||
          (column.fieldID === "EstimatedPaymentDate" && isInsuranceClaim)
        ) {
          return null;
        }

        let displayLabel = !lodash.isEmpty(column.format)
          ? parseTemplate(column.format, getFormattedValueMap(item, intl))(item)
          : "";

        if (column.type === "Currency") {
          return (
            <StandardMoney key={column.fieldID} value={displayLabel} currency={currencyType} />
          );
        }

        if (lodash.isEmpty(column.format) && item[humps.camelize(column.fieldID)]) {
          if (
            column.fieldID !== "Id" &&
            column.fieldID !== "CreateOn" &&
            column.fieldID !== "ReceiptDate" &&
            column.fieldID !== "ReceiptAmount" &&
            column.fieldID !== "Provider"
          ) {
            displayLabel = intl.formatMessage({
              id: item[humps.camelize(column.fieldID)],
              defaultMessage: item[humps.camelize(column.fieldID)],
            });
          } else {
            displayLabel = intl.formatMessage({
              id: item[humps.camelize(column.fieldID)],
              defaultMessage: item[humps.camelize(column.fieldID)]
            })
          }
        }

        if (newUIFlag && column.fieldID === "Status") {
          const claimStatus = item["statusIcon"];
          return renderStatusIcon(claimStatus, displayLabel, intl);
        }

        if (typeof displayLabel == "string") {
          displayLabel = renderAsHTML("span", displayLabel);
        }

        if (column.fieldID !== "Id") {
          return displayLabel;
        }

        return type === "expenseClaims" &&
          employeeDelegator &&
          String(employeeDelegator.employeeKey) === selectedDelegator &&
          employeeDelegator.role === "Manager" ? (
          <button className="link" onClick={() => handleReviewClaim(employeeDelegator)}>
            {displayLabel}
          </button>
        ) : (
          <Link key={column.fieldID} to={pathView}>
            {displayLabel}
          </Link>
        );
      });

      const editPath =
        selectedDelegator &&
        selectedDelegator.toLowerCase() !== "myself" &&
        selectedDelegator.toLowerCase() !== "me"
          ? `/${getDonateCategory ? "donations" : "claims"}/edit/${objectId}/${selectedDelegator}`
          : `/${getDonateCategory ? "donations" : "claims"}/edit/${objectId}`;

      row.push(
        <div className="u-nowrap">
          {type === "expenseClaims" &&
          employeeDelegator &&
          String(employeeDelegator.employeeKey) === selectedDelegator &&
          employeeDelegator.role === "Manager" ? (
            <button className="link" onClick={() => handleReviewClaim(employeeDelegator)}>
              {intl.formatMessage(messages.claims.reviewClaimDescription)}
            </button>
          ) : (
            <Fragment>
              {editable && (
                <div>
                  <Link to={editPath}>{intl.formatMessage(messages.editDescription)}</Link>
                  {deletable && " | "}
                  {deletable && (
                    <button className="link" onClick={() => action.delete(item.id, objectId)}>
                      {intl.formatMessage(messages.deleteDescription)}
                    </button>
                  )}
                </div>
              )}
            </Fragment>
          )}
        </div>
      );
      return row;
    });

    return (
      <div className="apl-scroll-table">
        <DataTable headers={headers} rows={rows} />
      </div>
    );
  }
);

export default PendingClaimsTable;



// WEBPACK FOOTER //
// ./components/claim/widgets/PendingClaimsTable.js